import { createContext, useContext, useEffect, useState } from "react";
import {
  FestivalCompetitionTab,
  RabbitFestivalPage,
  useRabbitFestivalTab,
} from "./use-festival-tab";
import { useRabbitFestivalMain } from "../components/main/hooks/use-festival";
import { PubTournament } from "../../../../api/types";
import { RunnerCompetitionDto } from "../../../../api/runner-competition";
import { NewsResponse } from "../../../../api/news";
import { useRabbitFestivalInterview } from "../components/interview/hooks/use-festival-interview";
import { useRabbitFestivalCompetition } from "../components/competition/hooks/use-festival-competition";
import { useRabbitFestivalEvent } from "../components/event/hooks/use-festival-event";
import { CompetitionTournamentResponse } from "../../../../api/runner-competition-tournament";

interface RabbitFestivalContextType
  extends ReturnType<typeof useRabbitFestivalTab> {
  showHeader: boolean;
  setShowHeader: (v: boolean) => void;
  tournaments: PubTournament[];
  competitions: RunnerCompetitionDto[];
  mainLoading: boolean;
  interviewLoading: boolean;
  interview: NewsResponse | null;
  setInterview: (v: NewsResponse | null) => void;
  interviewId: number;
  setInterviewId: (v: number) => void;
  interviewList: NewsResponse[];
  topInterview?: NewsResponse;
  year: number;
  setYear: (v: number) => void;
  interviewIsFirst: boolean;
  competition: RunnerCompetitionDto | null;
  setCompetition: (v: RunnerCompetitionDto | null) => void;
  competitionId: number;
  setCompetitionId: (v: number) => void;
  events: PubTournament[];
  competitionLoading: boolean;
  handleCompetitionPage: (id: number) => void;
  event: CompetitionTournamentResponse | null;
  setEvent: (v: CompetitionTournamentResponse | null) => void;
  eventId: number;
  setEventId: (v: number) => void;
  eventLoading: boolean;
  handleEventPage: (id: number) => void;
  getEvent: () => void;
}

const RabbitFestivalContext = createContext<RabbitFestivalContextType | null>(
  null
);

export const RabbitFestivalProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  // 탭
  const {
    festivalPage,
    setFestivalPage,
    isFestivalPage,
    mainTab,
    setMainTab,
    isMainTab,
    competitionTab,
    setCompetitionTab,
    isCompetitionTab,
  } = useRabbitFestivalTab();
  // 해더 표시 여부
  const [showHeader, setShowHeader] = useState(true);
  // 메인 배너
  const {
    tournaments,
    competitions,
    loading: mainLoading,
  } = useRabbitFestivalMain();
  // 인터뷰 관련
  const {
    interview,
    setInterview,
    interviewId,
    setInterviewId,
    interviewList,
    topInterview,
    year,
    setYear,
    isFirst: interviewIsFirst,
    loading: interviewLoading,
  } = useRabbitFestivalInterview();
  // 대회 페이지 관련
  const {
    competition,
    setCompetition,
    competitionId,
    setCompetitionId,
    events,
    loading: competitionLoading,
  } = useRabbitFestivalCompetition();
  // 이벤트 페이지 관련
  const {
    eventId,
    setEventId,
    event,
    setEvent,
    loading: eventLoading,
    getEvent,
  } = useRabbitFestivalEvent();

  // 페이지 이동
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const interview = urlParams.get("interview");
    const competition = urlParams.get("competition");
    const event = urlParams.get("event");

    // 인터뷰 페이지
    if (interview) {
      setInterviewId(+interview);
      setFestivalPage(RabbitFestivalPage.INTERVIEW);

      // 이동 후 url에서 삭제
      const url = new URL(window.location.href);
      url.searchParams.delete("interview");
      window.history.replaceState({}, "", url);
    }
    // 대회 페이지
    if (competition) {
      setCompetitionId(+competition);
      setFestivalPage(RabbitFestivalPage.COMPETITION);

      // 이동 후 url에서 삭제
      const url = new URL(window.location.href);
      url.searchParams.delete("competition");
      window.history.replaceState({}, "", url);
    }
    // 이벤트 페이지
    if (event) {
      setEventId(+event);
      setFestivalPage(RabbitFestivalPage.EVENT);

      // 이동 후 url에서 삭제
      const url = new URL(window.location.href);
      url.searchParams.delete("event");
      window.history.replaceState({}, "", url);
    }
  }, []);

  // 대회 페이지 이동
  const handleCompetitionPage = (id: number) => {
    setCompetitionId(id);
    setFestivalPage(RabbitFestivalPage.COMPETITION);
    setCompetitionTab(FestivalCompetitionTab.EVENT);
  };

  // 이벤트 페이지 이동
  const handleEventPage = (id: number) => {
    setEventId(id);
    setFestivalPage(RabbitFestivalPage.EVENT);
  };

  const value = {
    // 페이지
    festivalPage,
    setFestivalPage,
    isFestivalPage,
    // 메인탭
    mainTab,
    setMainTab,
    isMainTab,
    // 대회탭
    competitionTab,
    setCompetitionTab,
    isCompetitionTab,
    showHeader,
    setShowHeader,
    // 메인 페이지 배너(토너먼트), 대회 스와이프
    tournaments,
    competitions,
    mainLoading,
    // 인터뷰 페이지
    interview,
    setInterview,
    interviewId,
    setInterviewId,
    interviewLoading,
    // 메인 페이지 (인터뷰)
    interviewList,
    topInterview,
    year,
    setYear,
    interviewIsFirst,
    // 대회 페이지
    competition,
    setCompetition,
    competitionId,
    setCompetitionId,
    events,
    competitionLoading,
    handleCompetitionPage,
    // 이벤트 페이지
    eventId,
    setEventId,
    event,
    setEvent,
    eventLoading,
    handleEventPage,
    getEvent,
  };

  return (
    <RabbitFestivalContext.Provider value={value}>
      {children}
    </RabbitFestivalContext.Provider>
  );
};

export const useRabbitFestivalContext = () => {
  const context = useContext(RabbitFestivalContext);
  if (!context) {
    throw new Error("useContext must be used within a GameProvider");
  }
  return context;
};
